import { memo } from 'react';
import PostCard from '@/components/Post/PostCard';
import { Skeleton } from '@/components/ui/skeleton';
import ProjectGroupCard from '@/components/ProjectGroup/ProjectGroupCard';
import { getDisplayName } from '@/lib/utils';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { IFeedItem } from '@/redux/feed/feed.d';

const GRID_CLASS =
  'grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-5 py-2 w-full';

// Render one unified feed row as the right card, narrowing on `type`. The
// interleave order is whatever the backend returned (array order).
function renderFeedItem(item: IFeedItem, userId: string) {
  if (item.type === 'projectGroup') {
    return (
      <ProjectGroupCard
        key={`group-${item._id}`}
        id={item._id}
        name={item.name}
        description={item.description}
        thumbnail={item.thumbnail}
        likesCount={item.likesCount}
        commentsCount={item.commentsCount}
        createdAt={item.createdAt}
        hasLikedByUser={item.hasLikedByUser}
        hasBookmarkedByUser={item.hasBookmarkedByUser}
        isOwner={item.user._id === userId}
        authorUsername={item.user.username}
        authorName={getDisplayName(item.user)}
        authorAvatar={item.user.profilePicture}
        visibility={item.visibility}
        isOnefinityOfficial={item.user.isOnefinityOfficial}
        isTrustedMember={item.user.isTrustedMember}
        partnerData={item.partner}
        category={item.category}
        difficulty={item.difficulty}
        averageTotalRating={item.averageTotalRating}
        totalMakes={item.totalMakes}
        materialLinks={item.materialLinks}
        materialUsed={item.materialUsed}
        youtubeUrl={item.youtubeUrl}
      />
    );
  }

  return (
    <PostCard
      key={`post-${item._id}`}
      id={item._id}
      authorName={getDisplayName(item.user)}
      authorAvatar={item.user.profilePicture}
      authorUsername={item.user.username}
      title={item.title}
      likesCount={item.likes}
      commentsCount={item.comments}
      postThumbnail={item.postThumbnail ?? null}
      downloadCount={item.downloadCount ?? 0}
      createdAt={item.createdAt}
      category={item.category}
      description={item.description}
      hasLikedByUser={item.hasLikedByUser}
      isOnefinityOfficial={item.user.isOnefinityOfficial}
      isTrustedMember={item.user.isTrustedMember}
      isOwner={item.user._id === userId}
      visibility={item.visibility}
      partnerData={item.partner}
      hasBookmarkedByUser={item.hasBookmarkedByUser}
      averageTotalRating={item.averageTotalRating}
      totalMakes={item.totalMakes}
      isDimensionalMaterial={item.isDimensionalMaterial}
      difficulty={item.difficulty}
      materialUsed={item.materialUsed}
      youtubeUrl={item.youtubeUrl}
      materialLink={item.materialLink}
      materialLinks={item.materialLinks}
    />
  );
}

function UserPostsList({
  userId,
  items,
  isLoading,
  hasMore = false,
  isFetchingMore = false,
  onLoadMore,
}: {
  userId: string;
  // Unified feed rows (posts + project groups), already interleaved by the API.
  items: IFeedItem[];
  isLoading: boolean;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  // Returns a promise so InfiniteScroll's internal re-entry guard can await it.
  onLoadMore?: () => Promise<void>;
}) {
  if (isLoading)
    return (
      <div className={GRID_CLASS}>
        {Array.from({ length: 12 }).map((_, index) => (
          <Skeleton key={`skeleton-${index}`} className="w-full h-[420px]" />
        ))}
      </div>
    );

  if (items.length === 0)
    return (
      <div className="flex items-center justify-center h-[500px]">
        <p className="text-sm text-gray-500">No posts found</p>
      </div>
    );

  const grid = (
    <div className={GRID_CLASS}>
      {items
        .filter((item) => item !== null && item !== undefined)
        .map((item) => renderFeedItem(item, userId))}
    </div>
  );

  // When the parent supports cursor pagination, wrap the grid in InfiniteScroll
  // so the sentinel auto-fetches the next page (gated on hasMore + not loading).
  if (onLoadMore) {
    return (
      <InfiniteScroll
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        isLoading={isFetchingMore}
      >
        {grid}
      </InfiniteScroll>
    );
  }

  return grid;
}

// Memoized so the card grid doesn't re-render when an unrelated parent state
// (e.g. the debounced search input value) changes between fetches.
export default memo(UserPostsList);
